import { CSSProperties, JSX, ReactElement } from "react";
import React from "react";
import { ZoomExtentIcon } from "../../external/sx-icons/src/icons";
import { JSONDebugPrint } from "../helpers/JSONDebugPrint";

// todo(Gustav): document all coordinate spaces and make sure we only use one name
// drawing/world: for issuing drawing commands
// svg/display: the actual svg coordinate system

export type Point = [number, number]; // x, y

type PathMove = {
  type: "M";
  absolute: Point;
};

type PathLine = {
  type: "L";
  absolute: Point;
};

type PathCubic = {
  type: "C";
  start: Point;
  end: Point;
  absolute: Point;
};

type PathClose = {
  type: "Z";
};

type PathCubicSmooth = {
  type: "S";
  control: Point;
  absolute: Point;
};

type PathQuadraticBezier = {
  type: "Q";
  control: Point;
  absolute: Point;
};

type RadiusTuple = [number, number];
type ArcRadius = number | RadiusTuple;
const radius_tuple_from_arch_radius = (r: ArcRadius): RadiusTuple => (Array.isArray(r) ? r : [r, r]);

type PathArc = {
  type: "A";
  radius: ArcRadius;
  rotation: number;
  large_arc: boolean;
  sweep: boolean;
  absolute: Point;
};

type PathItem = PathMove | PathLine | PathCubic | PathCubicSmooth | PathQuadraticBezier | PathArc | PathClose;

const string_from_path_item = (cam: Camera, path: PathItem): string => {
  const str = (p: Point) => `${world_to_svg_0(cam, p[0])},${world_to_svg_1(cam, p[1])}`;
  switch (path.type) {
    case "A": {
      const [rx, ry] = radius_tuple_from_arch_radius(path.radius);
      return `A ${rx} ${ry} ${path.rotation} ${path.large_arc ? 1 : 0} ${path.sweep ? 1 : 0} ${str(path.absolute)}`;
    }
    case "M":
      return `M ${str(path.absolute)}`;
    case "L":
      return `L ${str(path.absolute)}`;
    case "S":
      return `S ${str(path.control)} ${str(path.absolute)}`;
    case "C":
      return `C ${str(path.start)} ${str(path.end)} ${str(path.absolute)}`;
    case "Q":
      return `Q ${str(path.control)} ${str(path.absolute)}`;
    case "Z":
      return `Z`;
  }
  throw { message: "unsupported path item", str: JSON.stringify(path, null, 2), path };
};

// number = data is in pixel in view space
// units = value is in units in diagram space
type PixelOrScalable =
  | number
  | {
      type: "units";
      value: number;
    };

// naming convention:
//  - StyleFor<Foo> = grouped style for the <Foo> component, the user should use theese types
//  - <Foo>Style = composeable style for the <Foo> drawing aspect, theese types are implementation defined and shouldn't escape
export type StyleForRect = LineStyle & DashStyle & FillStyle;
export type StyleForCircle = LineStyle & DashStyle & FillStyle;
export type StyleForLine = LineStyle & DashStyle;
export type StyleForPath = LineStyle & DashStyle & FillStyle;
export type StyleForGraphLine = LineStyle;
export type StyleForGraphFill = FillColorStyle | null;
export type StyleForGraphZeroLine = DashStyle & LineStyle;
export type StyleForGraphPoint = GraphPointStyle;
export type StyleForText = LineColorStyle & TextSizeStyle;
export type StyleForMarker = LineColorStyle & FillColorStyle;

const string_from_path = (cam: Camera, path: PathItem[]): string =>
  path.map((item) => string_from_path_item(cam, item)).join(" ");

type GraphPointStyle = null | { color: LineStyle & FillColorStyle; radius: number };

interface LineColorStyle {
  lineColor: string | undefined;
}
interface TextSizeStyle {
  textSize: number | string | undefined;
}
interface LineThicknessStyle {
  thickness?: PixelOrScalable;
}
interface FillColorStyle {
  fill: string | null; // null = no fill
}

interface ClickableStyle {
  onClicked: (() => void) | undefined;
}
export type OnClickFunction = () => void;
interface ClickableArg {
  onClicked?: OnClickFunction;
}

type LineStyle = LineColorStyle & LineThicknessStyle & DashStyle;

type FillStyle = FillColorStyle;

type DrawArrowType<ArrowId> = ArrowId | "no arrow";
type ArrowStyle<ArrowId> = {
  startArrow: DrawArrowType<ArrowId>;
  endArrow: DrawArrowType<ArrowId>;
};
type ArrowArg<ArrowId> = {
  startArrow?: DrawArrowType<ArrowId>;
  endArrow?: DrawArrowType<ArrowId>;
};

type DashStyle = {
  dashed?: boolean;
  centre?: boolean;
};

type TitleData = {
  title: string | null;
};
type TitleArg = {
  title?: string;
};
type HideableData = {
  display: boolean;
};
type HideableArg = {
  display?: boolean;
};

export type LineDrawable<ArrowId> = {
  type: "line";
  from: Point;
  to: Point;
} & LineStyle &
  ArrowStyle<ArrowId> &
  ClickableStyle &
  TitleData &
  HideableData;

export type PathDrawable<ArrowId> = {
  type: "path";
  path: PathItem[] | string;
  /**
   * see HitAreaRectArg - null disables the feature, otherwise a transparent, clickable rect is
   * drawn over this path's bounding box, extended to also cover the listed drawables.
   */
  hitAreaRect: Drawable<ArrowId>[] | null;
} & LineStyle &
  ArrowStyle<ArrowId> &
  FillStyle &
  ClickableStyle &
  TitleData &
  HideableData;

export type GroupDrawable<ArrowId> = {
  type: "group";
  items: Drawable<ArrowId>[];
  absolute?: Offset; // if not set, it is [0,0]
} & ClickableStyle &
  HideableData;

export type RectDrawable = {
  type: "rect";
  absolute: Point;
  width: number;
  height: number;
  radius: PixelOrScalable | undefined;
} & LineStyle &
  FillStyle &
  ClickableStyle &
  TitleData &
  HideableData;

export type CircleDrawable = {
  type: "circle";
  absolute: Point;
  radius: number;
} & LineStyle &
  FillStyle &
  ClickableStyle &
  TitleData &
  HideableData;

/// to draw multiple lines use
/// <>
///   <TextLine>line 1</TextLine>
///   <TextLine>line 2</TextLine>
/// </>
export type DrawableText = React.ReactNode;
export const TextLine = (props: { children?: React.ReactNode }) => (
  <tspan x="0" dy="1.2em">
    {props.children}
  </tspan>
);
type TextHoriAlign = "left" | "center" | "right";
type TextVertAlign = "top" | "middle" | "baseline" | "bottom";
type TextRotation = "upward" | "downward" | "horizontal";
export type TextDrawable = {
  type: "text";
  text: DrawableText;
  absolute: Point; // insertion point, see alignment below
  horiAlign: TextHoriAlign;
  vertAlign: TextVertAlign;
  rotation: TextRotation;
  // offset in pixels
  horiOffset: number;
  vertOffset: number;
  id?: string;
} & LineColorStyle &
  TextSizeStyle &
  HideableData;
export interface ClockwiseRotation {
  degrees: number;
}
export type SymbolDrawable<ArrowId> = {
  type: "symbol";
  absolute: Point;
  symbol: ArrowId;
  rotation?: ClockwiseRotation;
  testId?: string;
} & LineColorStyle &
  FillColorStyle &
  HideableData;

export type Drawable<ArrowId> =
  | LineDrawable<ArrowId>
  | GroupDrawable<ArrowId>
  | PathDrawable<ArrowId>
  | RectDrawable
  | CircleDrawable
  | TextDrawable
  | SymbolDrawable<ArrowId>;

// Axis Aligned Bounding Box
interface Aabb {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}
interface Offset {
  dx: number;
  dy: number;
}
const zero_offset: Offset = { dx: 0, dy: 0 };

export const diagramPadding = 2.1;

const aabb_include = (aabb: Aabb | null, point: Point, offset: Offset): Aabb => {
  const [x, y] = point;
  if (aabb === null) {
    return { maxX: x + offset.dx, maxY: y + offset.dy, minX: x - offset.dx, minY: y - offset.dy };
  }

  return {
    maxX: Math.max(aabb.maxX, x + offset.dx),
    maxY: Math.max(aabb.maxY, y + offset.dy),
    minX: Math.min(aabb.minX, x + offset.dx),
    minY: Math.min(aabb.minY, y + offset.dy),
  };
};

const aabb_include_path_item = (aabb: Aabb | null, path: PathItem, offset: Offset): Aabb | null => {
  switch (path.type) {
    case "M":
      return aabb_include(aabb, path.absolute, offset);
    case "L":
      return aabb_include(aabb, path.absolute, offset);
    case "A":
      // todo(Gustav): improve aabb calculation for angles?
      return aabb_include(aabb, path.absolute, offset);
    // todo(Gustav): improve cubic point inclusion?
    case "S":
      return aabb_include(aabb, path.absolute, offset);
    case "C":
      return aabb_include(aabb, path.absolute, offset);
    case "Q":
      return aabb_include(aabb, path.absolute, offset);
    // todo(Gustav): handle more cases
  }
  return aabb;
};

const join_offset = (absolute: Offset, offset: Offset): Offset => {
  return { dx: offset.dx + absolute.dx, dy: offset.dy + absolute.dy };
};

const validate_saved_rect = (r: Aabb | undefined): Aabb | undefined => {
  if (!r) return undefined;

  // for some reason, some text aabb results in only NaN,
  // theese checks should guard when this happens
  if (isFinite(r.maxX) === false) return undefined;
  if (isFinite(r.minX) === false) return undefined;
  if (isFinite(r.maxY) === false) return undefined;
  if (isFinite(r.minY) === false) return undefined;

  return r;
};

function rec_aabb_include<ArrowId>(
  draws: Drawable<ArrowId>[],
  initial: Aabb | null,
  offset: Offset,
  cam: Camera,
  textSizes: SavedRect,
): Aabb | null {
  return draws.reduce((aabb, dr): Aabb | null => {
    switch (dr.type) {
      case "text": {
        const found = validate_saved_rect(textSizes[key_from_text(dr)]);
        if (!found) {
          return aabb_include(aabb, dr.absolute, offset);
        }
        // return aabb_include(aabb, dr.absolute, offset);
        const absX = dr.absolute[0] + offset.dx;
        const absY = dr.absolute[1] + offset.dy;
        const corners: Point[] = [
          [absX + svg_to_world_scale(cam, found.minX), absY + svg_to_world_scale(cam, found.minY)],
          [absX + svg_to_world_scale(cam, found.maxX), absY + svg_to_world_scale(cam, found.minY)],
          [absX + svg_to_world_scale(cam, found.maxX), absY + svg_to_world_scale(cam, found.maxY)],
          [absX + svg_to_world_scale(cam, found.minX), absY + svg_to_world_scale(cam, found.maxY)],
        ];
        const na = corners.reduce((acc, pt) => aabb_include(acc, pt, { dx: 0, dy: 0 }), aabb);
        return na;
      }
      case "line":
        return aabb_include(aabb_include(aabb, dr.from, offset), dr.to, offset);
      case "symbol":
        return aabb_include(aabb, dr.absolute, offset);
      case "path":
        if (typeof dr.path === "string") return aabb;
        return dr.path.reduce((prev, item) => aabb_include_path_item(prev, item, offset), aabb);
      case "group":
        return rec_aabb_include(dr.items, aabb, join_offset(dr.absolute ?? zero_offset, offset), cam, textSizes);
      case "rect":
        return aabb_include(
          aabb_include(aabb, dr.absolute, offset),
          [dr.absolute[0] + dr.width, dr.absolute[1] + dr.height],
          offset,
        );
      case "circle":
        return aabb_include(
          aabb_include(aabb, dr.absolute, join_offset(offset, { dx: -dr.radius, dy: -dr.radius })),
          dr.absolute,
          join_offset(offset, { dx: dr.radius, dy: dr.radius }),
        );
    }
  }, initial);
}

/**
 * A cheap hash of what a drawing actually contains, used to notice that the drawing changed.
 *
 * Deliberately value based rather than comparing the drawables by reference: the array is rebuilt
 * from scratch on every render, so identity tells us nothing.
 *
 * Only geometry, structure and visibility are folded in - not colors, titles or click handlers -
 * so hovering or selecting something highlights it without being reported as new content. The
 * bounding box alone is far too blunt a signal here, because the column outline usually dictates
 * the extents and swamps everything else in the drawing.
 */
const content_signature = <ArrowId,>(draws: Drawable<ArrowId>[]): number => {
  // FNV-1a, chosen for being a couple of instructions per value - this runs over every drawable
  let hash = 0x811c9dc5;
  const mix = (n: number) => {
    hash ^= n;
    hash = Math.imul(hash, 0x01000193);
  };
  // rounded before hashing so that meaningless float noise does not read as a change
  const num = (n: number | undefined | null) => mix(n === undefined || n === null ? 0 : Math.round(n * 1024));
  const str = (s: unknown) => {
    const text = typeof s === "string" ? s : typeof s === "number" ? `${s}` : "";
    for (let i = 0; i < text.length; i += 1) mix(text.charCodeAt(i));
  };
  const point = (p: Point) => {
    num(p[0]);
    num(p[1]);
  };

  const rec = (items: Drawable<ArrowId>[]) => {
    for (const dr of items) {
      str(dr.type);
      mix(dr.display ? 1 : 2);
      switch (dr.type) {
        case "line":
          point(dr.from);
          point(dr.to);
          break;
        case "path":
          if (typeof dr.path === "string") str(dr.path);
          else
            for (const item of dr.path) {
              str(item.type);
              if (item.type !== "Z") point(item.absolute);
            }
          break;
        case "group":
          if (dr.absolute) {
            num(dr.absolute.dx);
            num(dr.absolute.dy);
          }
          rec(dr.items);
          break;
        case "rect":
          point(dr.absolute);
          num(dr.width);
          num(dr.height);
          break;
        case "circle":
          point(dr.absolute);
          num(dr.radius);
          break;
        case "text":
          point(dr.absolute);
          // the text is a ReactNode, so this only captures plain string/number labels - which is
          // what numeric readouts (the values that actually change) are
          str(dr.text);
          break;
        case "symbol":
          point(dr.absolute);
          str(dr.symbol);
          num(dr.rotation?.degrees);
          break;
      }
    }
  };

  rec(draws);
  return hash;
};

const SvgText = ({
  cam,
  dr,
  onSize,
  textSizes,
}: {
  cam: Camera;
  dr: TextDrawable;
  textSizes: SavedRect;
  onSize?: (bb: DOMRect) => void;
}) => {
  // hack: the ref should be a "group" but that doesn't contain getBBox that we need and also is contained on the group
  // it is placed in a group so we don't have to deal with all the aabb calculation as suggested here https://stackoverflow.com/a/70320192/180307
  const ref = React.useRef<SVGTextElement>(null);
  React.useEffect(() => {
    if (!ref.current) return;
    const size = ref.current.getBBox();
    onSize?.(size);
  }, [dr]);
  // debug variables
  // const bb  = ref.current?.getBBox() ?? undefined;
  // let localAabb = textSizes[key_from_text(dr)];
  const rotationString = (() => {
    switch (dr.rotation) {
      case "horizontal":
        return "";
      case "downward":
        return "rotate(90)";
      case "upward":
        return "rotate(-90)";
      default:
        console.warn(`unhandled rotation for text ${dr.rotation}`);
        return "";
    }
  })();
  const textAnchor = ((): React.SVGAttributes<SVGTextElement>["textAnchor"] => {
    switch (dr.horiAlign) {
      case "left":
        return "start";
      case "right":
        return "end";
      case "center":
        return "middle";
      default:
        console.warn(`unhandled horizontal align for text ${dr.horiAlign}`);
        return "middle";
    }
  })();
  const dominantBaseline = ((): React.SVGAttributes<SVGTextElement>["dominantBaseline"] => {
    switch (dr.vertAlign) {
      case "top":
        return "text-before-edge";
      case "middle":
        return "middle";
      case "baseline":
        return "no-change";
      case "bottom":
        return "text-after-edge";
      default:
        console.warn(`unhandled horizontal align for text ${dr.horiAlign}`);
        return "middle";
    }
  })();

  return (
    <>
      {/* bb && <rect x={bb.x} y={bb.y}
      width={bb.width} height={bb.height} fill="rgb(50% 50% 50% / 50%)"/> */}
      {/* localAabb && <rect
      x={localAabb.minX + world_to_svg_0(cam, dr.absolute[0])}
      y={localAabb.minY + world_to_svg_1(cam, dr.absolute[1])}
      width={localAabb.maxX - localAabb.minX}
      height={localAabb.maxY - localAabb.minY}
      fill="rgb(50% 50% 50% / 50%)"/> */}
      <g ref={ref}>
        <text
          textAnchor={textAnchor}
          dominantBaseline={dominantBaseline}
          fontSize={dr.textSize ?? 12}
          fill={dr.lineColor ?? undefined}
          transform={`translate(${world_to_svg_0(cam, dr.absolute[0]) + dr.horiOffset} ${world_to_svg_1(cam, dr.absolute[1]) + dr.vertOffset}) ${rotationString}`}
          id={dr.id ?? undefined}
        >
          {dr.text}
        </text>
      </g>
    </>
  );
};

const pixels_from_unit = (value: PixelOrScalable | undefined, cam: Camera) =>
  value === undefined || typeof value === "number" ? value : cam.scale * value.value;

// A one-pixel-wide line is practically impossible to hit with the mouse, so lines that actually
// react to the pointer - they carry a hover title and/or a click handler - get a second, fully
// transparent line drawn underneath at this minimum width. It only widens the hit area; the
// visible line keeps its own thickness. A line already thicker than this keeps its own width, so
// its hit area never shrinks below the stroke the user can see.
const HIT_AREA_MIN_WIDTH_PX = 10;

const interactive = (dr: TitleData & ClickableStyle) => dr.title !== null || dr.onClicked !== undefined;

const hit_area_width = (width: number | undefined) => Math.max(width ?? 1, HIT_AREA_MIN_WIDTH_PX);

/** Grows a screen-space interval so it is at least HIT_AREA_MIN_WIDTH_PX wide, keeping its center. */
const expand_to_min_hit_size = (min: number, max: number): [number, number] => {
  const missing = HIT_AREA_MIN_WIDTH_PX - (max - min);
  if (missing <= 0) return [min, max];
  return [min - missing / 2, max + missing / 2];
};

/**
 * Screen-space rect covering a path's bounding box, used as its hit area when the path opted in
 * via `hitAreaRect` (see drawPath). A thin, mostly-empty path - the L-shaped view direction hooks
 * in the cross-section view, say - is awkward to hit even with a widened stroke, because only the
 * stroke itself reacts; a rect over the whole bounding box lets the user click anywhere near the
 * symbol, including the empty space it encloses.
 *
 * The drawables listed in `hitAreaRect` are included in the bounding box without being drawn here,
 * so a path can extend its hit area over related geometry - typically its own label, which the
 * user is just as likely to aim for as the symbol itself.
 *
 * Returns null when there is nothing to cover or the path can't be measured (a raw `d` string).
 * Bounds are computed in the path's own coordinates, so any enclosing group transform applies to
 * the rect and the path alike.
 */
const hit_area_rect_for_path = <ArrowId,>(cam: Camera, dr: PathDrawable<ArrowId>, textSizes: SavedRect) => {
  if (dr.hitAreaRect === null || !interactive(dr)) return null;
  if (typeof dr.path === "string") return null;

  const pathBounds = dr.path.reduce<Aabb | null>((prev, item) => aabb_include_path_item(prev, item, zero_offset), null);
  const bounds = rec_aabb_include(dr.hitAreaRect, pathBounds, zero_offset, cam, textSizes);
  if (bounds === null) return null;

  // the stroke straddles the path, so half of it sits outside the bounding box of the points
  const pad = (pixels_from_unit(dr.thickness, cam) ?? 1) / 2;
  const [x1, x2] = expand_to_min_hit_size(
    world_to_svg_0(cam, bounds.minX) - pad,
    world_to_svg_0(cam, bounds.maxX) + pad,
  );
  const [y1, y2] = expand_to_min_hit_size(
    world_to_svg_1(cam, bounds.minY) - pad,
    world_to_svg_1(cam, bounds.maxY) + pad,
  );
  return { x: x1, y: y1, width: x2 - x1, height: y2 - y1 };
};

type TextSizeCallback = (text: TextDrawable, rect: Aabb) => void;

// recursive drawing of svg
function rec_draw_svg<ArrowId>(
  cam: Camera,
  draws: Drawable<ArrowId>[],
  textSizes: SavedRect,
  tsc: TextSizeCallback,
): JSX.Element[] {
  const clickableStyle = (click: ClickableStyle): CSSProperties | undefined =>
    click.onClicked !== undefined ? { cursor: "pointer" } : undefined;
  const clickable = (click: ClickableStyle) => click.onClicked;
  const marker = (arrow: DrawArrowType<ArrowId>) => (arrow !== "no arrow" ? `url(#${arrow})` : undefined);
  const symbolHref = (arrow: DrawArrowType<ArrowId>) => (arrow !== "no arrow" ? `#${arrow}-sym` : undefined);
  const strokeDasharray = (dash: DashStyle) => (dash.dashed ? "4 4" : dash.centre ? "25 10 5 10" : undefined);
  const stroke = (stroke: LineColorStyle) => stroke.lineColor;
  // arrowFill is from the css2 spec, from what I can tell it's not officially supported but seems to work in both forefox and edge(so probably also chrome)
  // setting fill for the arrow doesn't work when a path is "closed" but without fill, should we just give up on "global" markers and duplicate them per line/path?
  // const arrowFill = (stroke: LineColorStyle & ArrowStyle<ArrowId>) => stroke.startArrow !== "no arrow" || stroke.endArrow !== "no arrow" ? {fill: stroke.lineColor} : undefined;
  const fill = (fill: FillStyle) => fill.fill ?? "none";
  const strokeWidth = (thickness: LineThicknessStyle) => pixels_from_unit(thickness?.thickness, cam);
  const transform = (trans: GroupDrawable<ArrowId>) =>
    trans.absolute
      ? `translate(${world_to_svg_scale(cam, trans.absolute.dx)}, ${world_to_svg_scale(cam, trans.absolute.dy)})`
      : undefined;
  const add_title = (title: TitleData) => title.title && <title>{title.title}</title>;

  return draws.map((dr, dr_index): JSX.Element => {
    if (!dr.display) return <React.Fragment key={dr_index}></React.Fragment>;
    switch (dr.type) {
      case "line": {
        const x1 = world_to_svg_0(cam, dr.from[0]);
        const y1 = world_to_svg_1(cam, dr.from[1]);
        const x2 = world_to_svg_0(cam, dr.to[0]);
        const y2 = world_to_svg_1(cam, dr.to[1]);
        // the visible stroke is often only 1px wide, making it hard to hover/click precisely -
        // an invisible, wider "hit area" line underneath carries the click handler and title
        // tooltip so hovering near the line (not just exactly on top of it) still works. Only
        // lines that actually react to the pointer get one: a transparent stroke is still
        // hit-tested, so giving every decorative line a 10px band would let it swallow hover and
        // clicks meant for whatever is drawn beneath it.
        return (
          <g key={dr_index}>
            {interactive(dr) && (
              <line
                onClick={clickable(dr)}
                style={{ ...clickableStyle(dr) }}
                stroke="transparent"
                strokeWidth={hit_area_width(strokeWidth(dr))}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
              >
                {add_title(dr)}
              </line>
            )}
            <line
              style={{ pointerEvents: "none" }}
              stroke={stroke(dr)}
              strokeWidth={strokeWidth(dr)}
              strokeDasharray={strokeDasharray(dr)}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              markerStart={marker(dr.startArrow)}
              markerEnd={marker(dr.endArrow)}
            />
          </g>
        );
      }
      case "symbol": {
        const mx = world_to_svg_0(cam, dr.absolute[0]);
        const my = world_to_svg_1(cam, dr.absolute[1]);
        const t =
          dr.rotation !== undefined
            ? `translate(${mx} ${my}) rotate(${dr.rotation.degrees})`
            : `translate(${mx} ${my})`;
        return (
          <use
            key={dr_index}
            data-testid={dr.testId}
            color={stroke(dr)}
            fill={fill(dr)}
            transform={t}
            href={symbolHref(dr.symbol)}
          />
        );
      }
      case "text":
        return (
          <SvgText
            textSizes={textSizes}
            key={dr_index}
            cam={cam}
            dr={dr}
            onSize={(bb) => {
              // transform svg bounding box to a aabb centered on the text
              const minX = bb.x - world_to_svg_0(cam, dr.absolute[0]);
              const minY = bb.y - world_to_svg_1(cam, dr.absolute[1]);
              const localAabb = {
                minX,
                minY,
                // note: size is stored in screen space and doesn't have to be resized
                maxX: minX + bb.width,
                maxY: minY + bb.height,
              };
              if (validate_saved_rect(localAabb) === undefined) {
                console.log("local aabb is bad");
              }
              tsc(dr, localAabb);
            }}
          />
        );
      case "path": {
        // todo(Gustav): expose the fill rule?
        const hitArea = hit_area_rect_for_path(cam, dr, textSizes);
        const path = (
          <path
            key={dr_index}
            fill={fill(dr)}
            onClick={hitArea ? undefined : clickable(dr)}
            style={hitArea ? { pointerEvents: "none" } : { ...clickableStyle(dr) }}
            stroke={stroke(dr)}
            strokeWidth={strokeWidth(dr)}
            strokeDasharray={strokeDasharray(dr)}
            d={typeof dr.path === "string" ? dr.path : string_from_path(cam, dr.path)}
            markerStart={marker(dr.startArrow)}
            markerEnd={marker(dr.endArrow)}
            fillRule="evenodd"
          >
            {hitArea ? undefined : add_title(dr)}
          </path>
        );
        if (hitArea === null) return path;
        return (
          <g key={dr_index}>
            <rect
              onClick={clickable(dr)}
              style={{ ...clickableStyle(dr) }}
              fill="transparent"
              x={hitArea.x}
              y={hitArea.y}
              width={hitArea.width}
              height={hitArea.height}
            >
              {add_title(dr)}
            </rect>
            {path}
          </g>
        );
      }
      case "rect":
        return (
          <rect
            key={dr_index}
            fill={fill(dr)}
            onClick={clickable(dr)}
            style={clickableStyle(dr)}
            rx={pixels_from_unit(dr.radius, cam)}
            ry={pixels_from_unit(dr.radius, cam)}
            x={world_to_svg_0(cam, dr.absolute[0])}
            y={world_to_svg_1(cam, dr.absolute[1])}
            width={world_to_svg_scale(cam, dr.width)}
            height={world_to_svg_scale(cam, dr.height)}
            stroke={stroke(dr)}
            strokeWidth={strokeWidth(dr)}
            strokeDasharray={strokeDasharray(dr)}
          >
            {add_title(dr)}
          </rect>
        );
      case "circle":
        return (
          <circle
            key={dr_index}
            fill={fill(dr)}
            onClick={clickable(dr)}
            style={clickableStyle(dr)}
            cx={world_to_svg_0(cam, dr.absolute[0])}
            cy={world_to_svg_1(cam, dr.absolute[1])}
            r={world_to_svg_scale(cam, dr.radius)}
            stroke={stroke(dr)}
            strokeWidth={strokeWidth(dr)}
            strokeDasharray={strokeDasharray(dr)}
          >
            {add_title(dr)}
          </circle>
        );
      case "group":
        return (
          <g onClick={clickable(dr)} style={clickableStyle(dr)} transform={transform(dr)} key={dr_index}>
            {rec_draw_svg(cam, dr.items, textSizes, tsc)}
          </g>
        );
    }
  });
}

function rec_padding<ArrowId>(draws: Drawable<ArrowId>[], markers: SvgMarkers<ArrowId>, cam: Camera): number[] {
  const calculatePadding = (thickness: LineThicknessStyle, arrow: DrawArrowType<ArrowId>, cam: Camera) => {
    if (arrow === "no arrow") return 0;
    const padding = markers.find((x) => x.id === arrow)?.padding;
    if (padding === undefined) return 0;
    const thick = pixels_from_unit(thickness?.thickness, cam) ?? 1;
    const ret = thick * padding;
    return ret;
  };

  return draws.flatMap((dr): number[] => {
    switch (dr.type) {
      case "line":
        return [calculatePadding(dr, dr.startArrow, cam), calculatePadding(dr, dr.endArrow, cam)];
      case "path":
        return [calculatePadding(dr, dr.startArrow, cam), calculatePadding(dr, dr.endArrow, cam)];
      case "symbol":
        return [calculatePadding({}, dr.symbol, cam)];
      case "rect":
        return [];
      case "text":
        return [];
      case "circle":
        return [];
      case "group":
        return rec_padding(dr.items, markers, cam);
    }
  });
}

/// utils for transforming position/scale from canvas world to svg screen
interface Camera {
  offset: Point;
  scale: number;

  width?: number;
  height?: number;
  available_width?: number;
  available_height?: number;
}
const world_to_svg_0 = (cam: Camera, world: number): number => world * cam.scale + cam.offset[0];
const world_to_svg_1 = (cam: Camera, world: number): number => world * cam.scale + cam.offset[1];
const world_to_svg_scale = (cam: Camera, world: number): number => world * cam.scale;
const svg_to_world_scale = (cam: Camera, svg: number): number => svg / cam.scale;

const create_camera = ({
  clientWidth,
  clientHeight,
  aabb,
  inset,
}: {
  clientWidth: number;
  clientHeight: number;
  aabb: Aabb;
  inset: number;
}) => {
  // maxe sure when calculating zoom, the camera is...
  const min_world_size = 0.0000000001; // larger than 0 when looking at the smallest world/drawing size
  const min_screen_size = 1; // at least a 1x1 image when we're drawing the final image

  const width = Math.max(min_world_size, aabb.maxX - aabb.minX);
  const height = Math.max(min_world_size, aabb.maxY - aabb.minY);
  const available_width = Math.max(min_screen_size, clientWidth - inset * 2);
  const available_height = Math.max(min_screen_size, clientHeight - inset * 2);

  const scale = Math.min(available_width / width, available_height / height);
  const cam: Camera = {
    offset: [-aabb.minX * scale + inset, -aabb.minY * scale + inset],
    scale: scale,

    width,
    height,
    available_width,
    available_height,
  };
  if (Number.isNaN(scale)) {
    console.log("scale is nan");
  }
  if (scale === 0) {
    console.log("scale is zero");
  }
  return cam;
};

const str_from_aabb = (a: Aabb) => `${a.minX} - ${a.maxX} | ${a.minY} - ${a.maxY}`;

/**
 * A user-controlled pan/zoom applied *on top of* the automatic zoom-to-fit camera, in screen
 * space: a fitted screen point q becomes `q * zoom + pan`.
 *
 * Keeping it in screen space (rather than folding it into the fit) is what makes the interaction
 * maths trivial - a drag is a straight screen-space translation, so the world point under the
 * cursor cannot drift - and it leaves the fitted camera untouched, so the auto-fit result stays
 * available as the thing "reset" returns to.
 */
export interface ViewTransform {
  zoom: number;
  pan: Point;
}

export const identityViewTransform: ViewTransform = { zoom: 1, pan: [0, 0] };

const isIdentityViewTransform = (v: ViewTransform) => v.zoom === 1 && v.pan[0] === 0 && v.pan[1] === 0;

// world -> svg is `w * scale + offset`, so composing the screen-space `q * zoom + pan` on top of
// the fitted camera folds down to a plain camera again: no extra transform is needed at draw time.
const apply_view_transform = (cam: Camera, view: ViewTransform): Camera => ({
  ...cam,
  scale: cam.scale * view.zoom,
  offset: [cam.offset[0] * view.zoom + view.pan[0], cam.offset[1] * view.zoom + view.pan[1]],
});

const MIN_VIEW_ZOOM = 0.02;
const MAX_VIEW_ZOOM = 200;

const MIDDLE_MOUSE_BUTTON = 1;

/** How much one notch of the wheel zooms by. */
const ZOOM_PER_WHEEL_NOTCH = 1.1;

/**
 * Converts a wheel event into notches, positive being "zoom in".
 *
 * Wheel deltas are wildly inconsistent - the unit depends on deltaMode, a mouse notch is 100 or
 * 120 pixels depending on the browser, and a trackpad emits a stream of small deltas - so the
 * delta is normalised to notches and then clamped, which keeps a single gesture from jumping the
 * whole zoom range on hardware that reports large values.
 */
const wheel_notches = (e: WheelEvent, viewportHeight: number) => {
  const PIXELS_PER_NOTCH = 100;
  const LINES_PER_NOTCH = 3;
  const perNotch =
    e.deltaMode === 1 ? LINES_PER_NOTCH : e.deltaMode === 2 ? 1 : viewportHeight > 0 ? PIXELS_PER_NOTCH : 1;
  // scrolling up gives a negative deltaY and should zoom in
  const notches = -e.deltaY / perNotch;
  return Math.max(-1, Math.min(1, notches));
};

const clamp_zoom = (zoom: number) => Math.min(MAX_VIEW_ZOOM, Math.max(MIN_VIEW_ZOOM, zoom));

/**
 * Zoom about a fixed screen point, so whatever sits under the cursor stays under the cursor.
 *
 * The fitted position of that point, q = (p - pan) / zoom, does not depend on the view transform,
 * so holding `p = q * zoom' + pan'` fixed gives the new pan directly.
 */
const zoom_view_at = (view: ViewTransform, point: Point, factor: number): ViewTransform => {
  const zoom = clamp_zoom(view.zoom * factor);
  if (zoom === view.zoom) return view;
  const qx = (point[0] - view.pan[0]) / view.zoom;
  const qy = (point[1] - view.pan[1]) / view.zoom;
  return { zoom, pan: [point[0] - qx * zoom, point[1] - qy * zoom] };
};

function render_svg<ArrowId>(
  draw: Drawable<ArrowId>[],
  clientWidth: number,
  clientHeight: number,
  markers: SvgMarkers<ArrowId>,
  textSizes: SavedRect,
  tsc: TextSizeCallback,
  view: ViewTransform,
) {
  let aabb: Aabb = {
    minX: 0,
    minY: 0,
    maxX: 100,
    maxY: 100,
  };
  // text sizes are in screen space, so they are converted to world space with the current camera.
  // starting from the guessed aabb above, each iteration only shrinks the world size of a text by
  // (text pixels / available pixels), so a wide label in a small viewport is nowhere near converged
  // after the few iterations below. seed the loop with a text-free aabb (no saved sizes = only the
  // text anchor points are included) so the first camera is already in the right ballpark.
  {
    const seed = rec_aabb_include(
      draw,
      null,
      zero_offset,
      create_camera({
        clientWidth,
        clientHeight,
        aabb,
        inset: 10,
      }),
      {},
    );
    if (seed === null) return null;
    aabb = seed;
  }
  // when calculating the aabb, assume a basic aabb for text scaling purposes as a start and iterate a few times
  for (let i = 0; i < 5; i += 1) {
    const found = rec_aabb_include(
      draw,
      null,
      zero_offset,
      create_camera({
        clientWidth,
        clientHeight,
        aabb,
        inset: 10,
      }),
      textSizes,
    );
    if (found === null) return null;
    aabb = found;
  }

  const inset = Math.max(
    8,
    ...rec_padding(
      draw,
      markers,
      // assume a inset of 10 when figuring out the inset
      // todo(Gustav): should this be moved to the above ugly "few iterations" loop?
      create_camera({ clientWidth, clientHeight, aabb, inset: 10 }),
    ),
  );

  // Should we use svg viewbox instead of a custom camera?
  // A pure "viewbox" zoom would change the width of lines, size of text/labels and alter the dashing
  // aka how a magnifying glass would work. I don't think that it is desireable to change how
  // large a text label should be just because the column/beam went from 1m to 4m.
  // ...but I'm not 100% sure on this and can see both ways.
  //
  // The user's pan/zoom deliberately follows the same rule: it rides on top of the fitted camera,
  // so zooming in shows more of the drawing rather than magnifying the labels and line widths.
  // The fitted camera is kept as-is for the aabb/inset work above so that panning and zooming
  // cannot feed back into the fit (text is measured in screen space, so a zoom-dependent fit would
  // drift every frame).
  const cam = create_camera({ clientWidth, clientHeight, aabb, inset });
  const drawCam = apply_view_transform(cam, view);
  const svg = (
    <svg className="w-100 h-100" style={{ overflow: "visible" }}>
      <defs>{markers.map((x) => x.generate())}</defs>
      {rec_draw_svg(drawCam, draw, textSizes, tsc)}
    </svg>
  );

  return { aabb, inset, cam, drawCam, svg };
}

interface SingleMarker<ArrowId> {
  id: ArrowId;
  padding: number;
  generate: () => ReactElement;
}
type SvgMarkers<Id> = SingleMarker<Id>[];

// start: --|> (arrow starts at line end)
// end:   -->| (arrow ends at line end)
// middle: (between start and end)
type ArrowOrigin = "start" | "end" | "middle";

export const BasicArrowPath = (h: number) => {
  const ext = 1.0; // how far the arrow extend beyond the line (percent)
  const ver = 0.75; // how wide the arrow is (percent)
  return new PathBuilder()
    .moveTo([-h, -h * ver])
    .drawLineTo([h * ext, 0])
    .drawLineTo([-h, h * ver])
    .closePath();
};

// note: svg markers point to the right
type PathBuilderFunction = (size: number) => PathBuilder;
export function ArrowDefinition<ArrowId extends string>(
  id: ArrowId,
  size: number,
  origin: ArrowOrigin,
  pathBuilder: PathBuilderFunction,
  strokeWidth: number = 1,
): SingleMarker<ArrowId> {
  return {
    id,
    padding: size / 2,
    generate: () => (
      <ArrowDefinitionMarker<ArrowId>
        key={id}
        id={id}
        size={size}
        origin={origin}
        pathBuilder={pathBuilder}
        strokeWidth={strokeWidth}
      />
    ),
  };
}

function ArrowDefinitionMarker<ArrowId extends string>(props: {
  id: ArrowId;
  size: number;
  origin: ArrowOrigin;
  strokeWidth: number;
  pathBuilder: PathBuilderFunction;
}) {
  // fill="context-stroke" is from the css2 spec
  const refX = (() => {
    switch (props.origin) {
      case "start":
        return 0;
      case "middle":
        return 5;
      case "end":
        return 10;
    }
  })();
  const h = props.size / 2;
  const markerPath = string_from_path({ offset: [5, 5], scale: 1 }, props.pathBuilder(5).toPath());
  const symbolPath = string_from_path({ offset: [0, 0], scale: 1 }, props.pathBuilder(h).toPath());
  return (
    <>
      <marker
        id={props.id}
        key={props.id}
        viewBox="0 0 10 10"
        refX={refX}
        refY="5"
        fill="context-stroke"
        markerWidth={props.size}
        markerHeight={props.size}
        orient="auto-start-reverse"
      >
        <path d={markerPath} />
      </marker>
      <symbol id={`${props.id}-sym`} overflow="visible">
        <path d={symbolPath} fill="inherit" stroke="currentColor" strokeWidth={props.strokeWidth} />
      </symbol>
    </>
  );
}

export type IncludeDiagonal = "extension+diagonal" | "extension" | "point";
export function MeasureDefinition<ArrowId extends string>(
  id: ArrowId,
  size: number,
  diagonal: IncludeDiagonal,
): SingleMarker<ArrowId> {
  return {
    id,
    padding: size / 2,
    generate: () => <MeasureDefinitionMarker<ArrowId> key={id} id={id} size={size} diagonal={diagonal} />,
  };
}

// note: svg markers point to the right
export function MeasureDefinitionMarker<ArrowId extends string>(props: {
  id: ArrowId;
  size: number;
  diagonal: IncludeDiagonal;
}) {
  // stroke="context-stroke" is from the css2 spec
  //
  // design the svg at 20x20 and rescale to requested size
  // otherwise the lines will scale along with the requested markersize
  const s = props.size / 20;
  const z = props.diagonal === "point" ? 10 : 14;
  return (
    <>
      <marker
        id={props.id}
        viewBox={`0 0 ${20 * s} ${20 * s}`}
        refX={5 * s}
        refY={10 * s}
        fill="none"
        stroke="context-stroke"
        markerWidth={props.size}
        markerHeight={props.size}
        orient="auto-start-reverse"
      >
        <path d={`M${5 * s},${10 * s} L${19 * s},${10 * s} `} />
        {props.diagonal === "extension+diagonal" && <path d={`M${9 * s},${5 * s} L${1 * s},${15 * s}`} />}
      </marker>
      <symbol id={`${props.id}-sym`} overflow="visible">
        <path d={`M ${-z * s},0 L ${2 * z * s},0`} stroke="currentColor" fill="none" />
        {props.diagonal === "extension+diagonal" && (
          <path d={`M ${4 * s},${-5 * s} L ${-4 * s},${5 * s}`} stroke="currentColor" fill="none" />
        )}
      </symbol>
    </>
  );
}

export function CircleDefinition<ArrowId extends string>(
  id: ArrowId,
  size: number,
  strokeWidth: number = 1,
): SingleMarker<ArrowId> {
  return {
    id,
    padding: size / 2,
    generate: () => <CircleMarker<ArrowId> key={id} id={id} size={size} strokeWidth={strokeWidth} />,
  };
}
function CircleMarker<ArrowId extends string>(props: { id: ArrowId; size: number; strokeWidth: number }) {
  const r = props.size / 2;
  return (
    <>
      <marker
        id={props.id}
        viewBox={`${-r} ${-r} ${2 * r} ${2 * r}`}
        refX={0}
        refY={0}
        stroke="context-stroke"
        fill="context-fill"
        markerWidth={props.size}
        markerHeight={props.size}
        orient="auto-start-reverse"
      >
        <circle cx={0} cy={0} r={r} strokeWidth={props.strokeWidth} />
      </marker>
      <symbol id={`${props.id}-sym`} overflow="visible">
        <circle cx={0} cy={0} r={r} fill="inherit" stroke="currentColor" strokeWidth={props.strokeWidth} />
      </symbol>
    </>
  );
}

export function TickDefinition<ArrowId extends string>(
  id: ArrowId,
  size: number,
  strokeWidth: number = 1,
): SingleMarker<ArrowId> {
  return {
    id,
    padding: 0,
    generate: () => <TickMarker<ArrowId> key={id} id={id} size={size} strokeWidth={strokeWidth} />,
  };
}
function TickMarker<ArrowId extends string>(props: { id: ArrowId; size: number; strokeWidth: number }) {
  const h = props.size / 2;
  return (
    <>
      <marker
        id={props.id}
        viewBox={`${-h} ${-h} ${2 * h} ${2 * h}`}
        refX={0}
        refY={0}
        fill="none"
        stroke="context-stroke"
        markerWidth={props.size}
        markerHeight={props.size}
        orient="auto-start-reverse"
      >
        <line x1={0} y1={-h} x2={0} y2={h} strokeWidth={props.strokeWidth} />
      </marker>
      <symbol id={`${props.id}-sym`} overflow="visible">
        <line x1={0} y1={-h} x2={0} y2={h} stroke="currentColor" strokeWidth={props.strokeWidth} />
      </symbol>
    </>
  );
}

export class MinMax {
  min: number = Number.MAX_VALUE;
  max: number = -Number.MAX_VALUE;

  constructor(baseline: number | undefined = undefined) {
    if (baseline === undefined) return;

    this.min = baseline;
    this.max = baseline;
  }

  update = (value: number) => {
    if (value < this.min) {
      this.min = value;
    }
    if (value > this.max) {
      this.max = value;
    }
  };
}

// todo(Gustav): replace with a interface/object
//                      [0=z, 1=displayValueZ, 2=valZ, 3=x, 4=displayValueX, 5=valX]
export type ValueType = { verticalValue: number; horizontalValue: number };
type GraphOrientation = "horizontal" | "vertical";
function draw_graph<ArrowId>(
  values: ValueType[],
  // pass null to ignore zeroing out values close to zero
  userDecimals: number | null,
  line_style: LineStyle,
  fill_color: FillColorStyle | null,
  zero_line_color: (DashStyle & LineStyle) | null,
  orientation: GraphOrientation,
  scale: number,
  point_style: GraphPointStyle,
  displayGraph: boolean,
) {
  /* horizontal
    Z
    |
    |     .          /\
    |    / \        /  \
    |------------------------> X
    |        \____/
    |
  */
  const addFill = true;

  let rangex = new MinMax(0);
  let rangez = new MinMax(0);
  let points = new Array<Point>();
  // value "range" from zero line
  const value_range = Math.max(
    Math.abs(Math.min(...values.map((v) => v.verticalValue))),
    Math.abs(Math.max(...values.map((v) => v.verticalValue))),
  );

  // --------------------------------------------------------------------------------------------
  // add zero point where graph has intersected the z line
  (() => {
    // gather points where the graph intersects the line
    let extendedPoints = [];
    let prev_point_x = 0;
    let prev_point_z = 0;
    let pointOffset = 0;
    for (let i = 0; i < values.length; i++) {
      let dz = values[i].verticalValue;
      let x = values[i].horizontalValue;

      let zval = dz;
      let xval = x;

      let z_sign_has_changed = (zval > 0 && prev_point_z < 0) || (zval < 0 && prev_point_z > 0);

      if (z_sign_has_changed) {
        extendedPoints.push({
          x1: prev_point_x,
          z1: prev_point_z,
          x2: xval,
          z2: zval,
          pos: i + pointOffset,
        });
        pointOffset++;
      }
      prev_point_x = xval;
      prev_point_z = zval;
    }

    // and insert 0
    for (let i = 0; i < extendedPoints.length; i++) {
      let p = extendedPoints[i];
      let x = p.x1 - (p.z1 * (p.x2 - p.x1)) / (p.z2 - p.z1);
      values.splice(p.pos, 0, { horizontalValue: x, verticalValue: 0 });
    }
  })();

  // --------------------------------------------------------------------------------------------
  // create shapes
  const ret = new Array<Drawable<ArrowId>>();
  // Transform Point: if horizontal, then internal match actual, if vertical then swap x/z and offset
  // todo(Gustav): fix so that insertion point is at root
  const vertical_offset = 0; // values[values.length-1].x;
  const TP = (p: Point): Point => (orientation === "horizontal" ? p : [-p[1], -p[0] - vertical_offset]);

  let shapes = (() => {
    // Adds points for the perimeter part of the graph (i.e. not the fill)
    let addPointForDisplay = (i: number, valZ: number, valX: number) => {
      // Get the real values for the graph points

      // zero cleanup
      // Apply the user defined decimals
      if (userDecimals !== null && Number(valZ.toFixed(userDecimals)) === 0.0) {
        valZ = 0;
      }

      let x = valX;
      let z = valZ;
      points.push([x, z]);
      rangex.update(x);
      rangez.update(z);
    };

    // Now loop over the length of the beam and add points to both the perimeter and the fill
    type LineType = { Xcoord: number; Zcoord: number; id?: number; closed_id?: number };
    type ShapeType = { Xcoord: number; Zcoord: number; id?: number; lines: Array<LineType> };
    let shapes = new Array<ShapeType>();
    let currentShape = null; // current shape we are building
    let startCoordsZero = 0; // x coordinate where the current shape was started
    for (let i = 0; i < values.length; i++) {
      // Perimeter
      let valueZ = (values[i].verticalValue / value_range) * scale;
      // zero cleanup
      if ((userDecimals !== null && isNaN(valueZ)) || Number(values[i].verticalValue) === 0) {
        valueZ = 0;
      }

      let valueX = values[i].horizontalValue; // Horisontal deformation is not presented by decision!

      addPointForDisplay(i, valueZ, valueX);

      // Fill (skip for displacements, crack width and shear field force)
      if (addFill) {
        let roundedValueZ = valueZ;

        let crossing_x_axis_or_at_corners =
          Math.round(values[i].verticalValue * 1e3) / 1e3 === 0 || i === values.length - 1 || i === 0;

        if (crossing_x_axis_or_at_corners === false) {
          currentShape?.lines.push({
            Xcoord: values[i].horizontalValue,
            id: i,
            Zcoord: roundedValueZ,
          });
        } else {
          if (currentShape) {
            if (currentShape.lines.length > 0) {
              // if we started the shape... close it
              // A non-zero endpoint (first/last point, not a true zero-crossing) must contribute its
              // actual value to the fill, otherwise the polygon drops straight to the zero line here
              // and leaves a triangular wedge uncovered between the fill and the outline stroke.
              if (roundedValueZ !== 0) {
                currentShape.lines.push({
                  Xcoord: values[i].horizontalValue,
                  id: i,
                  Zcoord: roundedValueZ,
                });
              }
              currentShape.lines.push({
                Xcoord: values[i].horizontalValue,
                closed_id: i,
                Zcoord: 0.0,
              });
              currentShape.lines.push({
                Xcoord: startCoordsZero,
                closed_id: i,
                Zcoord: 0.0,
              });
            } else {
              // if we didn't start it... remove it
              // (this isn't strictly needed as we don't shapes with empty lines but it's nice)
              currentShape = null;
              shapes.pop();
            }
          }
          // replace current shape or create a new shape
          startCoordsZero = values[i].horizontalValue;
          currentShape = {
            Xcoord: startCoordsZero,
            Zcoord: roundedValueZ,
            id: i,
            lines: new Array<LineType>(),
          };
          shapes.push(currentShape);
        }
      }
    }
    return shapes;
  })();

  //---------------------------------------------------------------------------------------------
  // add shapes to the graph/world
  for (let i = 0; i < shapes.length; i++) {
    if (shapes[i].lines.length === 0) {
      continue;
    }

    let src = shapes[i];
    ret.push({
      type: "path",
      onClicked: undefined,
      path: [
        { type: "M", absolute: TP([src.Xcoord, src.Zcoord]) },
        ...src.lines.map((line) => {
          return { type: "L", absolute: TP([line.Xcoord, line.Zcoord]) } as PathItem;
        }),
        { type: "Z" },
      ],
      display: displayGraph,
      ...(fill_color ?? { fill: null }),
      startArrow: "no arrow",
      endArrow: "no arrow",
      lineColor: undefined,
      title: null,
      hitAreaRect: null,
    });
  }

  //---------------------------------------------------------------------------------------------
  // create closing lines for the fill

  for (let i = 0; i < 2; i++) {
    const index = i === 0 ? 0 : values.length - 1;
    if (Math.abs(points[index][1]) > 0.01) {
      ret.push({
        type: "line",
        onClicked: undefined,
        from: TP([points[index][0], 0.0]),
        to: TP([points[index][0], points[index][1]]),
        ...line_style,
        startArrow: "no arrow",
        endArrow: "no arrow",
        title: null,
        display: displayGraph,
      });
    }
  }

  //---------------------------------------------------------------------------------------------
  // create line

  ret.push({
    type: "path",
    onClicked: undefined,
    path: [
      ...points.map((p, index) => {
        return { type: index === 0 ? "M" : "L", absolute: TP(p) } as PathItem;
      }),
    ],
    ...line_style,
    startArrow: "no arrow",
    endArrow: "no arrow",
    fill: null,
    title: null,
    hitAreaRect: null,
    display: displayGraph,
  });

  //---------------------------------------------------------------------------------------------
  // add zero line if requested

  if (zero_line_color !== null) {
    const makePoint = (i: number) => TP([values[i]?.horizontalValue || 0, 0]);
    ret.push({
      type: "line",
      onClicked: undefined,
      from: makePoint(0),
      to: makePoint(values.length - 1),
      ...zero_line_color,
      startArrow: "no arrow",
      endArrow: "no arrow",
      title: null,
      display: displayGraph,
    });
  }

  // points group
  if (point_style !== null) {
    ret.push({
      type: "group",
      display: displayGraph,
      onClicked: undefined,
      items: points
        // Draw no circle for a point that sits on the zero line, except the first and last
        // point (the member ends), which always keep their circle.
        .map((p, index) => ({ p, index }))
        .filter(({ p, index }) => index === 0 || index === points.length - 1 || Math.abs(p[1]) > 1e-9)
        .map(({ p }): Drawable<ArrowId> => {
          return {
            type: "circle",
            onClicked: undefined,
            absolute: TP(p),
            ...point_style?.color,
            radius: point_style.radius,
            title: null,
            display: displayGraph,
          };
        }),
    });
  }

  return ret;
}

/**
 * A helper class for drawing a path. Do not put it in a state or a memo, use the resulting PathItem[].
 */
export class PathBuilder {
  items: PathItem[] = [];
  debug_isClosed = false;

  /**
   * M in svg
   */
  moveTo(absolute: Point) {
    this.debug_isClosed = false;
    this.items.push({ type: "M", absolute });
    return this;
  }

  /**
   * L in svg
   */
  drawLineTo(absolute: Point) {
    console.assert(this.debug_isClosed === false);
    this.items.push({ type: "L", absolute });
    return this;
  }

  /**
   * S in svg
   */
  drawCubicSmooth(absolute: Point, control: Point) {
    console.assert(this.debug_isClosed === false);
    this.items.push({ type: "S", control, absolute });
    return this;
  }

  /**
   * S in svg
   */
  drawArc(
    absolute: Point,
    radius: ArcRadius,
    opt: undefined | { large_arc?: boolean; rotation?: number; sweep?: boolean } = undefined,
  ) {
    console.assert(this.debug_isClosed === false);
    this.items.push({
      type: "A",
      radius,
      absolute,
      large_arc: opt?.large_arc ?? false,
      rotation: opt?.rotation ?? 0,
      sweep: opt?.sweep ?? false,
    });
    return this;
  }

  /**
   * C in svg
   */
  drawCubicBezier(absolute: Point, controls: { start: Point; end: Point }) {
    console.assert(this.debug_isClosed === false);
    this.items.push({ type: "C", ...controls, absolute });
    return this;
  }

  /**
   * Q in svg
   */
  drawQuadraticBezier(absolute: Point, control: Point) {
    console.assert(this.debug_isClosed === false);
    this.items.push({ type: "Q", control, absolute });
    return this;
  }

  /**
   * Z in svg
   */
  closePath() {
    console.assert(this.debug_isClosed === false);
    this.items.push({ type: "Z" });
    this.debug_isClosed = true;
    return this;
  }

  toPath() {
    return this.items;
  }

  // helpful functions
  drawCircle(absolute: Point, radius: number) {
    const rad = radius;
    const [h, v] = absolute;
    this.moveTo([h - rad, v]);
    this.drawArc([h + rad, v], rad);
    this.drawArc([h - rad, v], rad);
    this.closePath();
    return this;
  }
  drawRect(absolute: Point, width: number, height: number, options: { radius?: number } | undefined = undefined) {
    const rad = options?.radius ?? null;
    const [h, v] = absolute;
    if (rad) {
      this.moveTo([h, v + rad])
        .drawCubicSmooth([h + rad, v], [h, v]) // curve
        .drawLineTo([h + width - rad, v])
        .drawCubicSmooth([h + width, v + rad], [h + width, v]) // curve
        .drawLineTo([h + width, v + height - rad])
        .drawCubicSmooth([h + width - rad, v + height], [h + width, v + height]) // curve
        .drawLineTo([h + rad, v + height])
        .drawCubicSmooth([h, v + height - rad], [h, v + height]) // curve
        .closePath();
    } else {
      this.moveTo(absolute)
        .drawLineTo([h + width, v])
        .drawLineTo([h + width, v + height])
        .drawLineTo([h, v + height])
        .closePath();
    }
    return this;
  }
}

type OptionalArgs = ClickableArg & TitleArg & HideableArg;
type OptionalArgsWithArrow<ArrowId> = (OptionalArgs & ArrowArg<ArrowId>) | undefined;
/**
 * Opt-in for paths whose stroke is hard to hit: instead of relying on the stroke itself reacting
 * to the pointer, a transparent rect covering the path's bounding box takes the click handler and
 * the title tooltip. Use it for small, thin, mostly-empty symbols (see drawViewDirection.ts);
 * avoid it where the bounding box would cover unrelated geometry the user needs to interact with,
 * since the rect swallows the pointer over its whole area.
 *
 * `null` (the default) leaves the path as-is. An array turns the rect on and additionally extends
 * it to cover those drawables - pass the path's label, for example, so clicking the text works
 * too. The listed drawables are only measured, never drawn, so they still have to be added to the
 * drawer themselves (drawText returns the drawable it created for exactly this purpose).
 */
type HitAreaRectArg<ArrowId> = {
  hitAreaRect?: Drawable<ArrowId>[] | null;
};
const clickable_from_options = (options: ClickableArg | undefined): ClickableStyle =>
  options ? { onClicked: options.onClicked } : { onClicked: undefined };

/**
 * A helper class for drawing. Do not put it in a state or a memo, use the resulting Drawable[].
 *
 * Every draw function returns the drawable it created, so it can be handed to another drawable's
 * `hitAreaRect` (see drawPath). The returned drawable has already been added to the drawer; it is
 * the live object, so mutating it changes what gets drawn.
 */
export class Drawer<ArrowId> {
  drawables: Drawable<ArrowId>[] = [];

  drawRect(
    absolute: Point,
    width: number,
    height: number,
    style: StyleForRect,
    options: (OptionalArgs & { radius?: PixelOrScalable }) | undefined = undefined,
  ) {
    const drawable: RectDrawable = {
      type: "rect",
      absolute,
      width: Math.max(width, 0),
      height: Math.max(height, 0),
      ...style,
      radius: options?.radius,
      title: options?.title ?? null,
      display: options?.display ?? true,
      ...clickable_from_options(options),
    };
    this.drawables.push(drawable);
    return drawable;
  }

  drawCircle(absolute: Point, radius: number, style: StyleForCircle, options: OptionalArgs | undefined = undefined) {
    const drawable: CircleDrawable = {
      type: "circle",
      absolute,
      radius,
      ...style,
      title: options?.title ?? null,
      display: options?.display ?? true,
      ...clickable_from_options(options),
    };
    this.drawables.push(drawable);
    return drawable;
  }

  drawLine(
    from: Point,
    to: Point,
    style: StyleForLine,
    options: OptionalArgsWithArrow<ArrowId> | undefined = undefined,
  ) {
    const drawable: LineDrawable<ArrowId> = {
      type: "line",
      from,
      to,
      ...style,
      ...clickable_from_options(options),
      startArrow: options?.startArrow ?? "no arrow",
      title: options?.title ?? null,
      display: options?.display ?? true,
      endArrow: options?.endArrow ?? "no arrow",
    };
    this.drawables.push(drawable);
    return drawable;
  }

  drawPath(
    style: StyleForPath,
    pb: PathBuilder,
    options: (OptionalArgsWithArrow<ArrowId> & HitAreaRectArg<ArrowId>) | undefined = undefined,
  ) {
    const drawable: PathDrawable<ArrowId> = {
      type: "path",
      ...style,
      path: pb.toPath(),
      ...clickable_from_options(options),
      startArrow: options?.startArrow ?? "no arrow",
      endArrow: options?.endArrow ?? "no arrow",
      title: options?.title ?? null,
      hitAreaRect: options?.hitAreaRect ?? null,
      display: options?.display ?? true,
    };
    this.drawables.push(drawable);
    return drawable;
  }

  drawGraph(
    absolute: Point,
    orientation: GraphOrientation,
    scale: number,
    values: ValueType[],
    style: { line: StyleForGraphLine; fill: StyleForGraphFill; zero: StyleForGraphZeroLine },
    options:
      | ({
          point_style?: StyleForGraphPoint;
        } & OptionalArgs &
          HideableArg)
      | undefined = undefined,
  ) {
    const drawable: GroupDrawable<ArrowId> = {
      type: "group",
      display: options?.display ?? true,
      absolute: { dx: absolute[0], dy: absolute[1] },
      ...clickable_from_options(options),
      items: draw_graph(
        values,
        null,
        style.line,
        style.fill,
        style.zero,
        orientation,
        scale,
        options?.point_style ?? null,
        options?.display ?? true,
      ),
    };
    this.drawables.push(drawable);
    return drawable;
  }

  drawText(
    absolute: Point,
    text: DrawableText,
    style: StyleForText,
    opt:
      | ({
          id?: string;
          rotation?: TextRotation;
          horiAlign?: TextHoriAlign;
          vertAlign?: TextVertAlign;
          horiOffset?: number;
          vertOffset?: number;
        } & HideableArg)
      | undefined = undefined,
  ) {
    const drawable: TextDrawable = {
      type: "text",
      display: opt?.display ?? true,
      absolute,
      text,
      id: opt?.id ?? undefined,
      rotation: opt?.rotation ?? "horizontal",
      horiAlign: opt?.horiAlign ?? "center",
      vertAlign: opt?.vertAlign ?? "baseline",
      horiOffset: opt?.horiOffset ?? 0,
      vertOffset: opt?.vertOffset ?? 0,
      ...style,
    };
    this.drawables.push(drawable);
    return drawable;
  }

  // note: Markers point right, to point up use -90 and to point down use +90
  drawSymbol(absolute: Point, symbol: ArrowId, style: StyleForMarker, rotation?: ClockwiseRotation, testId?: string) {
    const drawable: SymbolDrawable<ArrowId> = {
      type: "symbol",
      absolute,
      symbol,
      rotation,
      testId,
      display: true,
      ...style,
    };
    this.drawables.push(drawable);
    return drawable;
  }

  toDrawables() {
    return this.drawables;
  }
}

interface SavedRect {
  // the aabb is stored in the local space of the drawable in a "screen space" scale
  [key: string]: Aabb | undefined;
}
// todo(Gustav): should we include the cam scale in this?
const key_from_text = (t: TextDrawable) => `${t.text}-${t.rotation}-${t.horiAlign}-${t.vertAlign}`;

/**
 * Mouse driven pan/zoom for a viewport: the wheel zooms about the cursor and a middle mouse drag
 * pans, both anchored so that whatever the cursor is over stays put.
 *
 * The result is a screen space ViewTransform rather than a camera, which is what keeps this
 * renderer agnostic - the svg views fold it into their fitted camera and the three.js view folds
 * it into an orthographic camera, but the interaction maths is identical and lives here only.
 *
 * `enabled` is false while a viewport zooms to fit on its own. The transform is then forced to
 * identity, though whatever the user had set is remembered in case the setting is turned back on.
 *
 * Callers own "the drawing changed" and should call `contentChanged` when that happens - only they
 * know what counts as a change. A changed drawing always returns to the fitted view, so newly
 * added or moved content remains visible.
 */
export function usePanZoom(enabled: boolean) {
  const ref = React.useRef<HTMLDivElement>(null);
  const [userView, setUserView] = React.useState<ViewTransform>(identityViewTransform);
  const drag = React.useRef<{ pointerId: number; last: Point } | null>(null);

  const view = enabled ? userView : identityViewTransform;

  // the browser treats wheel listeners as passive unless told otherwise and react gives us no way
  // to opt out, so this one is attached by hand to be allowed to preventDefault - without that the
  // page scrolls while zooming
  React.useEffect(() => {
    const div = ref.current;
    if (div === null || !enabled) return;

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = div.getBoundingClientRect();
      const point: Point = [e.clientX - rect.left, e.clientY - rect.top];
      const notches = wheel_notches(e, div.clientHeight);
      if (notches === 0) return;
      setUserView((old) => zoom_view_at(old, point, Math.pow(ZOOM_PER_WHEEL_NOTCH, notches)));
    };

    div.addEventListener("wheel", onWheel, { passive: false });
    return () => div.removeEventListener("wheel", onWheel);
  }, [enabled]);

  const reset = React.useCallback(() => {
    setUserView(identityViewTransform);
  }, []);

  const contentChanged = React.useCallback(() => setUserView(identityViewTransform), []);

  const startDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!enabled || e.button !== MIDDLE_MOUSE_BUTTON) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { pointerId: e.pointerId, last: [e.clientX, e.clientY] };
  };

  const moveDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    const active = drag.current;
    if (active === null || active.pointerId !== e.pointerId) return;
    const dx = e.clientX - active.last[0];
    const dy = e.clientY - active.last[1];
    active.last = [e.clientX, e.clientY];
    // a drag is a plain screen space translation, which is exactly what keeps the world point
    // grabbed by the cursor glued to the cursor
    setUserView((old) => ({ ...old, pan: [old.pan[0] + dx, old.pan[1] + dy] }));
  };

  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (drag.current === null || drag.current.pointerId !== e.pointerId) return;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    drag.current = null;
  };

  // an untouched view already shows everything, so there is nothing to reset and nothing to blink about
  const canReset = enabled && !isIdentityViewTransform(userView);

  return {
    enabled,
    view,
    reset,
    contentChanged,
    canReset,
    /** Attach to the element the gestures are measured against. */
    ref,
    /**
     * Merge into that element's style. Kept separate from `containerProps` because the submodule
     * and its consumers resolve different copies of csstype, so a CSSProperties-typed value does
     * not survive the trip across the package boundary.
     */
    gestureStyle: (enabled
      ? {
          // without this a touch drag scrolls the page instead of panning
          touchAction: "none",
          // panned/zoomed content would otherwise spill out over the neighbouring panels: the svg
          // itself draws with overflow visible on purpose (so markers and labels are not clipped
          // at the edge of the fitted view), which only stays inside the panel while the camera
          // is fitted to the content
          overflow: "hidden",
        }
      : {}) as { touchAction?: "none"; overflow?: "hidden" },
    /** Spread onto that same element. */
    containerProps: {
      ref,
      onPointerDown: startDrag,
      onPointerMove: moveDrag,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
      // chrome opens its autoscroll widget on middle mouse down unless the default is cancelled
      onMouseDown: (e: React.MouseEvent<HTMLDivElement>) => {
        if (enabled && e.button === MIDDLE_MOUSE_BUTTON) e.preventDefault();
      },
    },
  };
}

export type PanZoom = ReturnType<typeof usePanZoom>;

/** The grey the viewport panel's expand toggle uses, so the overlay buttons match it. */
const VIEWPORT_BUTTON_COLOR = "#727272";

/** Matches the viewport panel's expand toggle: no chrome, just the icon. */
const viewportButtonStyle = {
  background: "transparent",
  border: "none",
  padding: "2px",
  lineHeight: 0,
  color: VIEWPORT_BUTTON_COLOR,
  cursor: "pointer",
} as const;

/**
 * Returns the view to the automatic zoom-to-fit. Renders nothing while the viewport auto zooms -
 * there is nothing to reset then.
 *
 */
export const PanZoomResetButton = ({ panZoom }: { panZoom: PanZoom }) => {
  if (!panZoom.enabled) return null;

  return (
    <button
      style={{ ...viewportButtonStyle, opacity: panZoom.canReset ? 1 : 0.4 }}
      title="Reset pan and zoom so everything is visible"
      disabled={!panZoom.canReset}
      onClick={panZoom.reset}
    >
      <ZoomExtentIcon size={16} color={VIEWPORT_BUTTON_COLOR} />
    </button>
  );
};

/** The overlay the debug and reset buttons share, in the top left corner of a viewport. */
export const ViewportButtons = ({ children }: { children: React.ReactNode }) => (
  <div style={{ position: "absolute", left: "0", top: "0", display: "flex", gap: "2px", zIndex: 1 }}>{children}</div>
);

/** A borderless overlay button, styled to match the viewport panel's expand toggle. */
export const ViewportButton = (props: { title?: string; onClick: () => void; children: React.ReactNode }) => (
  <button
    style={{ ...viewportButtonStyle, fontSize: "12px", lineHeight: 1 }}
    title={props.title}
    onClick={props.onClick}
  >
    {props.children}
  </button>
);

export function Svg<ArrowId>(props: {
  markers: SvgMarkers<ArrowId>;
  items: Drawable<ArrowId>[];
  isDev: boolean;
  /** When true (the default) the view always zooms to fit. When false the user pans and zooms it. */
  autoZoom?: boolean;
}) {
  const autoZoom = props.autoZoom ?? true;
  const [isDebugView, setDebugView] = React.useState(false);
  const [clientSize, setClientSize] = React.useState<{ width: number; height: number } | null>(null);
  const [textSizes, setTextSizes] = React.useState<SavedRect>({});
  const panZoom = usePanZoom(!autoZoom);
  const mainContentAreaDiv = panZoom.ref;

  const save_rect = (text: TextDrawable, bb: Aabb) => {
    setTextSizes((old) => {
      const oldBB = old[key_from_text(text)];
      if (
        oldBB !== undefined &&
        oldBB.maxX === bb.maxX &&
        oldBB.maxX === bb.maxX &&
        oldBB.minY === bb.minY &&
        oldBB.maxY === bb.maxY
      ) {
        // console.log("Old was in database", oldBB, bb);
        return old;
      }

      const newRect: SavedRect = { ...old };
      newRect[key_from_text(text)] = bb;
      // console.log("Storing bb", bb);
      return newRect;
    });
  };

  React.useEffect(() => {
    const updateClientSize = () => {
      const c = mainContentAreaDiv.current;
      if (!c) return;
      setClientSize({
        width: c.clientWidth,
        height: c.clientHeight,
      });
    };

    // set current size
    if (!mainContentAreaDiv.current) return;
    updateClientSize();

    // resize on change
    const resizeObserver = new window.ResizeObserver(updateClientSize);
    resizeObserver.observe(mainContentAreaDiv.current);
    return () => {
      resizeObserver.disconnect();
    };
    // https://developer.mozilla.org/en-US/docs/Web/API/Resize_Observer_API
    // https://stackoverflow.com/a/39312522/180307
  }, []);

  const rendered =
    clientSize !== null
      ? render_svg(
          props.items,
          clientSize.width,
          clientSize.height,
          props.markers,
          textSizes,
          (text, bb) => {
            const aabb = validate_saved_rect(bb);
            if (aabb === undefined) {
              console.log("Ignored invalid aabb");
              return;
            }
            save_rect(text, aabb);
          },
          panZoom.view,
        )
      : null;

  // "the drawing changed" is decided from the drawables themselves, so it is independent of the
  // camera and the viewport: resizing the panel or panning around will not trip it, but editing
  // the column, its loads or its results will.
  const contentKey = content_signature(props.items);
  const lastContentKey = React.useRef(contentKey);
  const contentChanged = panZoom.contentChanged;
  React.useEffect(() => {
    if (contentKey === lastContentKey.current) return;
    lastContentKey.current = contentKey;
    contentChanged();
  }, [contentKey, contentChanged]);

  const body = isDebugView ? (
    <>
      <JSONDebugPrint
        value={{
          clientSize,
          textSizes,
          view: panZoom.view,
          items: props.items,
          rendered: { ...rendered, svg: "too slow for browsers" },
        }}
      />
    </>
  ) : (
    rendered?.svg
  );

  return (
    <div
      className="h-100 w-100 m-0 p-0"
      style={{ position: "relative", ...panZoom.gestureStyle }}
      {...panZoom.containerProps}
    >
      <ViewportButtons>
        {props.isDev && (
          <ViewportButton title="Toggle render debug view" onClick={() => setDebugView((old) => !old)}>
            ?
          </ViewportButton>
        )}
        <PanZoomResetButton panZoom={panZoom} />
      </ViewportButtons>
      {body}
    </div>
  );
}

export const lerp = (from: number, to: number, a: number) => from * (1 - a) + to * a;
